import { Injectable, ViewContainerRef } from '@angular/core';

import { Store } from '@ngrx/store';
import { deepEqual } from 'fast-equals';
import {
  asapScheduler,
  combineLatest,
  debounceTime,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { UserSelectors } from '@recruitee/account-data';
import type { User } from '@recruitee/account-types';
import { filterBoolean } from '@recruitee/common';
import { FeatureGuardFacade, GuardedFeature } from '@recruitee/feature-guard';
import { RoleSelectors } from '@recruitee/roles-data';
import { DialogService, DialogWidth } from '@recruitee/user-interface';
import type { Visibility } from '@recruitee/visibility-types';
import { VisibilityLevel } from '@recruitee/visibility-types';

import {
  VisibilityDialogComponent,
  VisibilityDialogData,
} from '../visibility-dialog/visibility-dialog.component';
import { VisibilitySelectorAnalytics } from './visibility-selector.analytics';

@Injectable()
export class VisibilitySelectorService {
  public featureVisibility$ = this.featureGuardFacade.checkFeatureAvailability(
    GuardedFeature.Visibility,
  );

  private usersData$ = combineLatest([
    this.store.select(UserSelectors.entities()).pipe(filterBoolean),
    this.store.select(RoleSelectors.entities()).pipe(filterBoolean),
  ]).pipe(
    debounceTime(0, asapScheduler),
    distinctUntilChanged(deepEqual),
    map(([admins, roles]) => {
      const groupedAdmins = admins.reduce<Record<string, User[]>>(
        (groups, admin) => ({
          ...groups,
          ...{ [admin.roleId]: [...(groups[admin.roleId] || []), admin] },
        }),
        {},
      );

      return {
        rolesWithAdmins: roles.map(r => ({
          ...r,
          id: r.id!,
          isAdmin: r.admin,
          admins: groupedAdmins[r.id!] || [],
          count: (groupedAdmins[r.id!] && groupedAdmins[r.id!].length) || 0,
        })),
        admins,
      };
    }),
  );

  constructor(
    private dialogService: DialogService,
    private vcRef: ViewContainerRef,
    private analytics: VisibilitySelectorAnalytics,
    private store: Store,
    private featureGuardFacade: FeatureGuardFacade,
  ) {}

  public onVisibilityChanges(
    level: VisibilityLevel,
    currentVisibility: Visibility,
    options: Omit<VisibilityDialogData, 'assignedRoles' | 'assignedAdmins'>,
  ): Observable<Visibility | undefined> {
    if (level === VisibilityLevel.Protected) {
      return this.dialogService
        .openCustom(
          VisibilityDialogComponent,
          { width: DialogWidth.SmallX },
          {
            assignedAdmins: currentVisibility.adminIds,
            assignedRoles: currentVisibility.roleIds,
            ...options,
          },
          this.vcRef,
        )
        .afterClosed.pipe(
          tap(value => {
            if (!value) return;

            this.analytics.trackChange({ level, selectedMembersCount: value.adminIds.length });
          }),
        );
    } else {
      if (currentVisibility.level !== level) {
        this.analytics.trackChange({ level, selectedMembersCount: 0 });
      }

      return of({ roleIds: [], level, adminIds: [] });
    }
  }

  public getVisibilityUsersData(visibility$: Observable<Visibility>) {
    return this.usersData$.pipe(
      switchMap(({ admins, rolesWithAdmins }) =>
        visibility$.pipe(
          map(visibility => ({
            admins: admins.filter(a => visibility.adminIds.includes(a.id)),
            rolesWithAdmins: rolesWithAdmins.filter(r => visibility.roleIds.includes(r.id)),
          })),
        ),
      ),
    );
  }
}
